import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';
import { StorageService } from 'src/app/Core/_services/storage/storage.service';
import { SeoService } from 'src/app/Shared/_services/seo/seo.service';
import { GeneralService } from 'src/app/_utlis/general/general.service';
import {
  ARABIC_COUNTRIES,
  DEFAULT_LOCALE,
  isValidLocale,
  LocaleCode,
} from './locale.constants';

@Component({
  selector: 'app-language-landing',
  standalone: true,
  imports: [RouterLink],
  template: `
    <main class="language-landing">
      <h1>BidxSell</h1>
      <p>Select your language</p>
      <nav class="language-links">
        <a [routerLink]="'/en'">English</a>
        <a [routerLink]="'/ar'">العربية</a>
      </nav>
    </main>
  `,
  styles: [
    `
      .language-landing {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 12px;
        font-family: Inter, sans-serif;
      }

      .language-links {
        display: flex;
        gap: 16px;
      }
    `,
  ],
})
export class LanguageLandingComponent implements OnInit {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private storageService: StorageService,
    private generalService: GeneralService,
    private seoService: SeoService
  ) {}

  ngOnInit(): void {
    this.seoService.setHreflangTags('/', DEFAULT_LOCALE);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.redirectToPreferredLocale();
  }

  private async redirectToPreferredLocale(): Promise<void> {
    const preferredLocale = await this.resolvePreferredLocale();
    this.router.navigateByUrl(`/${preferredLocale}`, { replaceUrl: true });
  }

  private async resolvePreferredLocale(): Promise<LocaleCode> {
    const localeFromStorage = this.getLocaleFromSelectedLanguage();
    if (localeFromStorage) {
      return localeFromStorage;
    }

    const countryFromCache =
      this.storageService.getMetaData()?.data_from_ip?.country_code_2;
    if (countryFromCache) {
      return this.localeFromCountry(countryFromCache);
    }

    const metadataResponse = await firstValueFrom(
      this.generalService
        .getMetadata()
        .pipe(catchError(() => of(null)))
    );

    const countryCode = metadataResponse?.data?.country_code_2;
    if (!countryCode) {
      return DEFAULT_LOCALE;
    }

    this.storageService.setMetaData({
      data_from_ip: metadataResponse.data,
    });
    return this.localeFromCountry(countryCode);
  }

  private getLocaleFromSelectedLanguage(): LocaleCode | null {
    const selectedLanguageRaw = localStorage.getItem(
      this.storageService.getPrefixedKey('selectedLang')
    );

    if (!selectedLanguageRaw) {
      return null;
    }

    try {
      const selectedLanguage = JSON.parse(selectedLanguageRaw);
      const localeCandidate = (
        selectedLanguage?.short_name ||
        selectedLanguage?.slug ||
        ''
      ).toLowerCase();

      return isValidLocale(localeCandidate) ? localeCandidate : null;
    } catch {
      return null;
    }
  }

  private localeFromCountry(countryCode: string): LocaleCode {
    return (ARABIC_COUNTRIES as readonly string[]).includes(
      countryCode.toUpperCase()
    )
      ? 'ar'
      : DEFAULT_LOCALE;
  }
}
